import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { Box, Tabs, Tab, CircularProgress } from "@mui/material";
import { Helmet } from "react-helmet-async";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import isEmpty from "lodash/isEmpty";

// Components
import InnerPageMobileHeader from "../../../components/reusable-components/inner-page-mobile-header/InnerPageMobileHeader";
import NoData from "../../../components/reusable-components/no-data/NoData";
import ContentHeader from "../../../components/reusable-components/dashboard-contents/content-header/ContentHeader";
import CoreButtons from "../../../components/core-components/button/CoreButtons";
import { MessagingContainer } from "../../../components/pages-components/b2b/messaging";
import OrderSummaryCard from "../../../components/pages-components/b2b/corporate-event-details/order-summary-card/OrderSummaryCard";
import JoinWhatsAppGroupModal from "../../../components/pages-components/join-whatsApp-group-modal/JoinWhatsAppGroupModal";
import EventDetailsSection from "../../../components/pages-components/b2b/event-details/EventDetailsSection";
import EventTabContent from "../../../components/pages-components/b2b/event-details/event-tab-content/EventTabContent";
import CollapsibleCard from "../../../components/pages-components/b2b/corporate-event-details/collapsible-card/CollapsibleCard";
import QuotesTabContent from "../../../components/pages-components/b2b/event-details/QuotesTabContent";
import DeclineEventModal from "../../../components/pages-components/decline-event-modal/DeclineEventModal";
import DeclineReasonBanner from "../../../components/pages-components/b2b/event-details/decline-reason-banner/DeclineReasonBanner";

// Hooks and Utils
import { useAppDispatch, useAppSelector } from "../../../hooks/storeHooks";
import { useToast } from "../../../context/ToasterContext";
import IsMobile from "../../../hooks/isMobile";
import { fDate } from "../../../utils/formatTime";
import { getDeclineReason, isEventDeclined } from "../../../utils/B2B/corporateEventsUtils";

// Store
import {
	declineCorporateEventThunk,
	getCorporateEventDetailThunk,
	getCorporateEventWhatsAppGroupLink,
	getCorporateEventWhatsAppGroupRecord,
} from "../../../store/b2b/corporateEvent/corporateEventThunk";
import {
	resetCorporateEventDetailApiError,
	setCorporateEventDetail,
} from "../../../store/b2b/corporateEvent/corporateEventSlice";
import { notificationCountThunk } from "../../../store/notifications/notificationThunk";

// Types and Constants
import {
	CorporateEventStatusEnum,
	CorporateEventVendorStatusEnum,
	RespondToCorporateEventInviteEnum,
	CorporateEventTabsEnum,
} from "../../../constants/enums";
import { BUTTON_COLOR, BUTTON_VARIANT } from "../../../types/coreButtonTypes";
import {
	CorporateEventDetailsTabs,
	ICorporateEvent,
	ICorporateEventWhatsAppGroupInfo,
} from "../../../interfaces/corporateEvent";
import { REDUX_REQUEST_STATUS } from "../../../types/commonTypes";
import { REMOVE_SPECIAL_CHAR_FROM_STRING } from "../../../utils/regex";
import { unwrapResult } from "@reduxjs/toolkit";
import { iType, WhatsAppGroupStatusEnum } from "../../../types/eventTypes";
import { ICorporateEventMessage } from "../../../interfaces/corporateEvent";
import { whatsAppModalVariations } from "../../../utils/content";
import { breadCrumbLinkAndTitleForCorporateEvent, scrollToSectionHelper } from "../../../helpers/common";
import { DECLINE_VENDOR_REASONS } from "../../../constants/formConstants";
import SendbirdChatContiner from "../../../components/pages-components/b2b/messaging/SendbirdChatContiner/SendbirdChatContiner";
import SendbirdEventMentionSync from "../../../components/SendBird/SendbirdEventMentionSync";
import { applyMentionFlagsToRows } from "../../../utils/sendbird/chatAccessibility";
import { useOnUpdateCorporateEvent } from "../../../hooks/firestoreHooks/corporateEventHook";
import { ROUTE_PATHS } from "../../../routes";
import useCheckScroll from "../../../hooks/useCheckScroll";

// Icons
import DeclineCorporateEventIcon from "../../../assets/svg-fc/b2b/DeclineCorporateEventIcon";

// Styles
import "./corporateEventDetails.scss";

const TOP_SPACING_THRESHOLD = 100;

const CorporateEventDetails: React.FC = () => {
	const navigate = useNavigate();
	const { showToast } = useToast();
	const isMobile = IsMobile();
	const params = useParams();
	const dispatch = useAppDispatch();
	const [searchParams, setSearchParams] = useSearchParams();
	const oldActiveTab = searchParams.get("tab") as CorporateEventTabsEnum;
	const [activeTab, setActiveTab] = useState<CorporateEventTabsEnum>(() => {
		const isValidStoredTab =
			Object.values(CorporateEventTabsEnum).includes(oldActiveTab) &&
			(isMobile || oldActiveTab !== CorporateEventTabsEnum.Messaging);
		if (isValidStoredTab) return oldActiveTab;
		return isMobile ? CorporateEventTabsEnum.Messaging : CorporateEventTabsEnum.Event;
	});
	const [trigger, setTrigger] = useState(false);
	const [isScrolling, setIsScrolling] = useState(false);
	const [loading, setLoading] = useState(false);
	const [isChatVisible, setIsChatVisible] = useState(false);
	const [whatsAppModalOpen, setWhatsAppModalOpen] = useState(false);
	const [whatsAppGroupLink, setWhatsAppGroupLink] = useState<string>("");
	const [isDeclineEventModalOpen, setIsDeclineEventModalOpen] = useState(false);
	const [declineReason, setDeclineReason] = useState<string>("");
	const [otherDeclineReason, setOtherDeclineReason] = useState<string>("");
	const [whatsAppBannerVisible, setWhatsappBannerVisible] = useState<boolean>(true);
	const [whatsAppGroupDetails, setWhatsAppGroupDetails] = useState<ICorporateEventWhatsAppGroupInfo>(
		{} as ICorporateEventWhatsAppGroupInfo,
	);
	const [shouldStartUpdatingEvent, setShouldStartUpdatingEvent] = useState(false);
	const [mentionFlagsByUrl, setMentionFlagsByUrl] = useState<Record<string, boolean>>({});

	// State for controlling collapsible cards expansion on mobile
	const [eventCardExpanded, setEventCardExpanded] = useState<boolean>(true);
	const [quotesCardExpanded, setQuotesCardExpanded] = useState<boolean>(true);

	// Refs for scroll detection
	const messagingRef = useRef<HTMLDivElement>(null);
	const eventRef = useRef<HTMLDivElement>(null);
	const quotesRef = useRef<HTMLDivElement>(null);
	const tabsRef = useRef<HTMLDivElement>(null);
	const pageLayoutRef = useRef<HTMLElement>(null);
	const expanded = useCheckScroll(
		pageLayoutRef as React.RefObject<HTMLElement>,
		TOP_SPACING_THRESHOLD,
		trigger,
	);

	// Redux state
	const {
		corporateEventDetail,
		corporateEventDetailLoading: isLoading,
		corporateEventDetailApiError: apiError,
	} = useAppSelector((state) => state.corporateEvent);
	const { userDetails } = useAppSelector((state) => state.user);

	useEffect(() => {
		if (corporateEventDetail) setTrigger(true);
	}, [corporateEventDetail]);

	const selectedChat: ICorporateEventMessage = corporateEventDetail?.messages?.[0] as ICorporateEventMessage;
	const rawMessages = corporateEventDetail?.messages ?? [];
	const messagesWithMentionFlags = useMemo(
		() => applyMentionFlagsToRows(rawMessages, mentionFlagsByUrl),
		[rawMessages, mentionFlagsByUrl],
	);
	const selectedChatWithMentions = messagesWithMentionFlags[0] ?? selectedChat;
	const shouldShowChat = !isMobile || isChatVisible;

	const handleMentionFlagsUpdate = useCallback((flags: Record<string, boolean>) => {
		setMentionFlagsByUrl((previous) => {
			let changed = false;
			const next = { ...previous };
			for (const [url, hasMention] of Object.entries(flags)) {
				if (previous[url] !== hasMention) {
					next[url] = hasMention;
					changed = true;
				}
			}
			return changed ? next : previous;
		});
	}, []);

	// Intersection Observer for scroll detection
	useEffect(() => {
		if (!isMobile) return;

		const thresholds = Array.from({ length: 21 }, (_, i) => i / 20); // 0..1 in steps of .05

		const observer = new IntersectionObserver(
			(entries) => {
				if (isScrolling) return;

				let mostVisibleId: CorporateEventTabsEnum | null = null;
				let maxRatio = 0;

				entries.forEach((entry) => {
					const sectionId = entry.target.getAttribute("data-section") as CorporateEventTabsEnum;
					if (!sectionId) return;

					if (entry.intersectionRatio > maxRatio) {
						maxRatio = entry.intersectionRatio;
						mostVisibleId = sectionId;
					}
				});

				if (mostVisibleId && mostVisibleId !== activeTab) {
					setActiveTab(mostVisibleId);
				}
			},
			{
				root: null,
				rootMargin: "0px 0px -30% 0px",
				threshold: thresholds,
			},
		);

		const sections = [
			{ ref: messagingRef, id: CorporateEventTabsEnum.Messaging },
			{ ref: eventRef, id: CorporateEventTabsEnum.Event },
			{ ref: quotesRef, id: CorporateEventTabsEnum.Quotes },
		];

		sections.forEach(({ ref, id }) => {
			if (ref.current) {
				ref.current.setAttribute("data-section", id);
				observer.observe(ref.current);
			}
		});

		return () => {
			observer.disconnect();
		};
	}, [isMobile, activeTab, isScrolling, corporateEventDetail]);

	useEffect(() => {
		if (selectedChat?.channelUrl && !isMobile) {
			handleChatClick();
		}
	}, [selectedChat?.channelUrl, isMobile]);

	useEffect(() => {
		if (params?.id) {
			getCorporateEventDetails();
			dispatch(notificationCountThunk());
		}

		return () => {
			dispatch(setCorporateEventDetail(null));
		};
	}, [params?.id, dispatch]);

	useEffect(() => {
		if (apiError) {
			showToast(apiError.message, () => onToasterClose(), "error");
		}
	}, [apiError, showToast]);

	const [loadedEventId, setLoadedEventId] = useState<string | null>(null);

	const getCorporateEventDetails = () => {
		if (!!params?.id) {
			const eventId = params.id;
			dispatch(getCorporateEventDetailThunk(eventId)).finally(() => setLoadedEventId(eventId));
			setShouldStartUpdatingEvent(true);
		}
	};

	useOnUpdateCorporateEvent(shouldStartUpdatingEvent, getCorporateEventDetails, params?.id || "");

	const onToasterClose = () => {
		dispatch(resetCorporateEventDetailApiError());
	};

	const handleGoBack = () => {
		if (window.history.length > 1) {
			navigate(-1);
		}
	};

	const handleChatClick = async () => {
		if (selectedChat) {
			if (selectedChat.channelUrl) {
				setMentionFlagsByUrl((previous) => ({ ...previous, [selectedChat.channelUrl]: false }));
			}
			setIsChatVisible(true);

			const corporateEventVendorId = selectedChat.corporateEventVendorId;
			const details = await dispatch(getCorporateEventWhatsAppGroupRecord({ corporateEventVendorId }));
			if (details.meta.requestStatus === REDUX_REQUEST_STATUS.FULFILLED) {
				const unwrappedResult = unwrapResult(details);
				setWhatsAppGroupDetails(unwrappedResult);
			} else {
				setWhatsAppGroupDetails({} as ICorporateEventWhatsAppGroupInfo);
			}

			const groupLink = await dispatch(getCorporateEventWhatsAppGroupLink({ corporateEventVendorId }));
			if (groupLink.meta.requestStatus === REDUX_REQUEST_STATUS.FULFILLED) {
				const unwrappedResult = unwrapResult(groupLink).data.invitationLink;
				setWhatsAppGroupLink(unwrappedResult);
			} else {
				setWhatsAppGroupLink("");
			}
		}
	};

	const handleShowNewQuoteForm = () => {
		navigate(`${ROUTE_PATHS.QUOTE_FORM}?corporateEventId=${corporateEventDetail?.id}`);
	};

	const handleCloseChatClick = () => {
		setIsChatVisible(false);
		setWhatsAppModalOpen(false);
	};

	const isUserPrivacyEnabled = useMemo(() => {
		if (whatsAppGroupDetails && selectedChat?.vendor?.vendorWhatsAppNumbers?.length) {
			const vendorWhatsAppNumbers = selectedChat?.vendor?.vendorWhatsAppNumbers.map((num) => {
				return num.replace(REMOVE_SPECIAL_CHAR_FROM_STRING, "");
			});
			return !whatsAppGroupDetails?.participants?.find((item) => vendorWhatsAppNumbers.includes(item?.id));
		} else return true;
	}, [whatsAppGroupDetails, selectedChat?.vendor?.vendorWhatsAppNumbers]);

	const handleTabChange = (newValue: CorporateEventTabsEnum) => {
		setActiveTab(newValue);

		// On mobile, expand the corresponding card if it's collapsed
		if (isMobile) {
			if (newValue === CorporateEventTabsEnum.Event && !eventCardExpanded) {
				setEventCardExpanded(true);
			} else if (newValue === CorporateEventTabsEnum.Quotes && !quotesCardExpanded) {
				setQuotesCardExpanded(true);
			}
		}

		setSearchParams(
			(prev) => {
				prev.set("tab", newValue);
				return prev;
			},
			{ replace: true },
		);

		scrollToSection(newValue);
	};

	const scrollToSection = (section: CorporateEventTabsEnum) => {
		setIsScrolling(true);

		scrollToSectionHelper(`corporateEventDetailsTag_${section}`);

		setTimeout(() => {
			setIsScrolling(false);
		}, 1000);
	};

	const renderTabContent = () => {
		// Render all content in a single scroll view
		return (
			<Box className="tabContentContainer">
				{/* Messaging Section */}
				<Box id="corporateEventDetailsTag_messaging" ref={messagingRef}>
					{renderMessagingSection()}
				</Box>

				{/* Event Section */}
				<Box id="corporateEventDetailsTag_event" ref={eventRef}>
					<CollapsibleCard
						headerTitle="Event Details"
						isExpanded={eventCardExpanded}
						onToggle={setEventCardExpanded}
					>
						<EventTabContent corporateEventDetail={corporateEventDetail as ICorporateEvent} />
					</CollapsibleCard>
				</Box>

				{/* Quotes Section */}
				<Box id="corporateEventDetailsTag_quotes" ref={quotesRef}>
					<CollapsibleCard
						headerTitle="Quotes"
						isExpanded={quotesCardExpanded}
						onToggle={setQuotesCardExpanded}
					>
						<QuotesTabContent
							corporateEventDetail={corporateEventDetail as ICorporateEvent}
							onClickAddAdditionalQuote={handleShowNewQuoteForm}
							isEventDeclined={isEventDeclined(corporateEventDetail as ICorporateEvent)}
						/>
					</CollapsibleCard>
				</Box>
			</Box>
		);
	};

	const updateStatesOnCancelOrDeclineEvent = () => {
		setIsDeclineEventModalOpen(false);
		setIsChatVisible(false);
		setWhatsAppGroupDetails({} as ICorporateEventWhatsAppGroupInfo);
		setWhatsAppGroupLink("");
		navigate(-1);
	};

	const handleDeclineEventClick = async () => {
		if (!declineReason) return showToast("Please select a reason", () => {}, "error");
		try {
			setLoading(true);

			const res = await dispatch(
				declineCorporateEventThunk({
					corporateEventId: corporateEventDetail?.id as string,
					body: {
						status: RespondToCorporateEventInviteEnum.Decline,
						declineReason: {
							value: otherDeclineReason
								? ""
								: DECLINE_VENDOR_REASONS.find((reason) => reason.FormControlLabelValue === declineReason)
										?.FormControlLabelLabel,
						} as iType,
						otherDeclineReason: otherDeclineReason,
					},
				}),
			);

			if (res.meta.requestStatus === REDUX_REQUEST_STATUS.FULFILLED) {
				showToast("Event has been declined");
				getCorporateEventDetails();
				updateStatesOnCancelOrDeclineEvent();
			} else {
				showToast("Something went wrong", () => {}, "error");
			}
			setLoading(false);
		} catch (error) {
			setLoading(false);
			showToast(error instanceof Error ? error.message : "Something went wrong", () => {}, "error");
		}
	};

	const showWhatsAppIcon = useMemo(() => {
		return (
			selectedChat?.isWhatsappGroupCreated &&
			whatsAppGroupDetails?.groupStatus === WhatsAppGroupStatusEnum?.active
		);
	}, [selectedChat?.isWhatsappGroupCreated, whatsAppGroupDetails?.groupStatus]);

	const showFooter =
		corporateEventDetail?.status === CorporateEventStatusEnum.Active &&
		corporateEventDetail?.vendor?.status === CorporateEventVendorStatusEnum.AvailableForBidding;

	const renderMessagingSection = () => {
		return (
			<>
				{!(shouldShowChat && selectedChat?.channelUrl) ? (
					<SendbirdEventMentionSync
						rows={rawMessages.map((message) => ({
							channelUrl: message.channelUrl,
							isNewModelChannel: message.isNewModelChannel,
						}))}
						vendorSendBirdId={corporateEventDetail?.vendorSendBirdId}
						onFlagsUpdate={handleMentionFlagsUpdate}
					/>
				) : null}
				{shouldShowChat && selectedChat?.channelUrl ? (
					<SendbirdChatContiner
						selectedChat={selectedChatWithMentions}
						vendorSendBirdId={corporateEventDetail?.vendorSendBirdId}
						showWhatsAppIcon={showWhatsAppIcon}
						showWhatsAppBanner={showWhatsAppIcon && whatsAppBannerVisible}
						setWhatsAppModalOpen={setWhatsAppModalOpen}
						handleCloseChatClick={handleCloseChatClick}
						showChatHeaderBackButton={false}
						setWhatsappBannerVisible={(value: boolean) => setWhatsappBannerVisible(value)}
						expanded={expanded}
						className={expanded && !showFooter ? "corporateEventChatContainer__expanded__noFooter" : ""}
					/>
				) : (
					<MessagingContainer chat={selectedChatWithMentions} onChatClick={handleChatClick} expanded={expanded} />
				)}
			</>
		);
	};

	const renderPageContent = () => {
		return (
			<>
				<Helmet>
					<title>{`Corporate Event Detail | ${corporateEventDetail?.name || "Loading..."}`}</title>
				</Helmet>
				<Box
					className="content"
					ref={pageLayoutRef}
					sx={{
						display: "flex",
						flexDirection: "column"
					}}>
					<Box className="pageLayoutCorporateEventDetail" sx={{
						flex: "1"
					}}>
						{!isMobile && (
							<ContentHeader
								contentHeaderTitle={"Event summary"}
								breadCrumbsLinkTitle={breadCrumbLinkAndTitleForCorporateEvent(corporateEventDetail)?.title}
								breadCrumbsHref={breadCrumbLinkAndTitleForCorporateEvent(corporateEventDetail)?.link}
								breadCrumbsCurrentTitle={corporateEventDetail?.name}
								ordNumber={corporateEventDetail?.orderNumber}
								createdBy="YouCater Corporate Requests"
							/>
						)}
						<Box className="corporateEventDetailsContainer">
							{isMobile ? (
								<Box className="orderSummaryAndMobileTabsContainer">
									<OrderSummaryCard
										createdBy="YouCater Corporate Requests"
										ordNumber={corporateEventDetail?.orderNumber as number}
									/>

									{isEventDeclined(corporateEventDetail as ICorporateEvent) && (
										<DeclineReasonBanner
											declineReason={getDeclineReason(corporateEventDetail as ICorporateEvent) || ""}
										/>
									)}

									{/* Mobile Tabs Navigation */}
									<Box className="mobileTabsContainer" ref={tabsRef}>
										<Tabs
											value={activeTab}
											onChange={(_, newValue) => {
												handleTabChange(newValue);
											}}
											variant="fullWidth"
										>
											<Tab label="Messaging" value={CorporateEventTabsEnum.Messaging} />
											<Tab label="Event" value={CorporateEventTabsEnum.Event} />
											<Tab label="Quotes" value={CorporateEventTabsEnum.Quotes} />
										</Tabs>

										{renderTabContent()}
									</Box>
								</Box>
							) : (
								<>
									{renderMessagingSection()}
									<EventDetailsSection
										selectedTab={activeTab}
										corporateEventDetail={corporateEventDetail as ICorporateEvent}
										setSelectedTab={(tab: CorporateEventTabsEnum) => {
											handleTabChange(tab);
										}}
										onClickAddAdditionalQuote={handleShowNewQuoteForm}
										isEventDeclined={isEventDeclined(corporateEventDetail as ICorporateEvent)}
										OnClickTriggerLeftContainerScroll={() => {
											setTrigger(true);
										}}
									/>
								</>
							)}
						</Box>
					</Box>
				</Box>
			</>
		);
	};

	if (isLoading || (!!params?.id && loadedEventId !== params.id)) {
		return (
			<Box className="corporateEventDetailLoaderContainer">
				<CircularProgress size={24} />
			</Box>
		);
	}

	return (
		<>
			{isMobile && shouldShowChat && selectedChat?.channelUrl ? (
				<SendbirdChatContiner
					className="corporateEventChatContainerMobileView"
					selectedChat={selectedChatWithMentions}
					vendorSendBirdId={corporateEventDetail?.vendorSendBirdId}
					showWhatsAppIcon={showWhatsAppIcon}
					showWhatsAppBanner={showWhatsAppIcon && whatsAppBannerVisible}
					setWhatsAppModalOpen={setWhatsAppModalOpen}
					handleCloseChatClick={handleCloseChatClick}
					setWhatsappBannerVisible={(value: boolean) => setWhatsappBannerVisible(value)}
					handleSendbirdMessageUpdates={getCorporateEventDetails}
				/>
			) : (
				<>
					{isMobile && (
						<InnerPageMobileHeader
							goBackOnClick={() => handleGoBack()}
							customerFirstLastName={"YC"}
							eventName={corporateEventDetail?.name || ""}
							eventDate={fDate(corporateEventDetail?.startDate as string)}
							customerName={"YouCater Corporate Requests"}
							declineEventIcon={<DeclineCorporateEventIcon />}
							declineEvent={
								corporateEventDetail?.status === CorporateEventStatusEnum.Active ? "Cancel Event" : ""
							}
							declineEventOnclick={() => {
								setDeclineReason("");
								setOtherDeclineReason("");
								setIsDeclineEventModalOpen(true);
							}}
						/>
					)}
					{!isEmpty(corporateEventDetail) ? renderPageContent() : <NoData sx={{ bgcolor: "#f2f2f2" }} />}

					{showFooter ? (
						<div className="footer footerCorporateEventDetails">
							{!isMobile ? (
								<CoreButtons
									buttonText="Decline Event"
									variant={BUTTON_VARIANT.TEXT}
									color={BUTTON_COLOR.ERROR}
									fullWidth={false}
									disabled={false}
									coreBtnPill={true}
									startIcon={<DeclineCorporateEventIcon />}
									onclick={() => {
										setDeclineReason("");
										setIsDeclineEventModalOpen(true);
									}}
								/>
							) : null}

							<CoreButtons
								className="footer-right"
								buttonText="Send a quote"
								variant={BUTTON_VARIANT.CONTAINED}
								fullWidth={false}
								disabled={false}
								coreBtnPill={true}
								onclick={() => {
									handleTabChange(CorporateEventTabsEnum.Quotes);
									handleShowNewQuoteForm();
								}}
							/>
						</div>
					) : null}
				</>
			)}

			<JoinWhatsAppGroupModal
				buttonText={
					isUserPrivacyEnabled
						? whatsAppModalVariations.joinGroup
						: whatsAppModalVariations.copyGroupNameBTNText
				}
				heading={whatsAppModalVariations.chatOnWhatsApp}
				subheading={whatsAppModalVariations.aGroupCalled}
				middleText={whatsAppGroupDetails?.name ?? ""}
				endingText={
					isUserPrivacyEnabled
						? whatsAppModalVariations.privacyEnabled.replace(
								"{codeBlock}",
								"<span style='font-weight:500;'>privacy settings</span>",
							)
						: whatsAppModalVariations.copyGroupNameText
				}
				closeModal={() => setWhatsAppModalOpen(false)}
				openWhatsAppModal={whatsAppModalOpen}
				disclaimer={!isUserPrivacyEnabled ? whatsAppModalVariations.ifYouHaveMoreThenOne : ""}
				onClickJoinWhatsApp={async () => {
					await navigator.clipboard.writeText(whatsAppGroupDetails.name);
					window.open(whatsAppGroupLink || whatsAppGroupDetails.invitationLink, "_blank");
				}}
			/>

			<DeclineEventModal
				modalHeaderText="Decline Event?"
				open={isDeclineEventModalOpen}
				onClose={() => setIsDeclineEventModalOpen(false)}
				declineModalBodyTitle="Please specify a reason in order to decline the event"
				buttonText="Confirm"
				variant={BUTTON_VARIANT.CONTAINED}
				color={BUTTON_COLOR.ERROR}
				fullWidth={true}
				coreBtnPill={true}
				disabled={!declineReason}
				onclick={() => handleDeclineEventClick()}
				RadioGroupValue={declineReason}
				RadioGroupHandleChange={(event: React.ChangeEvent<HTMLInputElement>) => {
					setDeclineReason(event.target.value);
				}}
				radioLabels={DECLINE_VENDOR_REASONS}
				otherOnChange={(event: React.ChangeEvent<HTMLInputElement>) => {
					setOtherDeclineReason(event.target.value);
				}}
				otherPlaceholder="Input a reason other than ones specified"
				loading={loading}
			/>
		</>
	);
};

export default CorporateEventDetails;
